import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import PolySphere from "@/components/PolySphere";
import PolyAnimal from "@/components/PolyAnimal";
import api from "@/lib/api";

const PILLAR_LETTERS = ["S", "F", "E", "P", "C"];
const PILLAR_NAMES = ["STRENGTH", "SPEED", "EXPLOSIVE", "STAMINA", "CONTROL"];

export default function Archetypes() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    api.get("/archetypes").then((r) => setList(r.data.archetypes || []));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "ALL") return list;
    const idx = PILLAR_LETTERS.indexOf(filter);
    return list.filter((a) => a.code[idx] === a.code[idx].toUpperCase());
  }, [list, filter]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 relative" data-testid="archetypes-page">
      <div className="absolute right-[-8%] top-[-2%] opacity-30 pointer-events-none hidden md:block">
        <PolySphere size={420} />
      </div>
      <div className="relative">
        <div className="gps-tag mb-4">IDENTITY MATRIX</div>
        <h1 className="gps-heading text-4xl md:text-6xl">32 ARCHETYPES</h1>
        <p className="text-mute max-w-2xl mt-6">
          Generated from five binary axes. Each archetype represents a unique configuration of
          physical capability — your biological performance signature. Tap any card to dive in.
        </p>

        <div className="flex flex-wrap gap-2 mt-10" data-testid="archetype-filters">
          <button
            onClick={() => setFilter("ALL")}
            className={`gps-btn ${filter === "ALL" ? "" : "ghost"}`}
            data-testid="filter-all"
          >
            ALL · 32
          </button>
          {PILLAR_LETTERS.map((L, i) => (
            <button
              key={L}
              onClick={() => setFilter(L)}
              className={`gps-btn ${filter === L ? "" : "ghost"}`}
              data-testid={`filter-${L}`}
            >
              {L} · {PILLAR_NAMES[i]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {filtered.map((a) => (
            <Link
              key={a.code}
              to={`/archetypes/${a.code}`}
              className="contents"
              data-testid={`archetype-card-${a.code}`}
            >
              <GlassPanel className="p-5 glow-hover cursor-pointer h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="gps-mono text-white tracking-[0.3em] text-sm">{a.code}</span>
                    <div className="gps-heading text-2xl mt-3 truncate text-white">{a.name.toUpperCase()}</div>
                    <div className="text-mute text-xs mt-1 truncate">{a.tag}</div>
                  </div>
                  {a.image_url && (
                    <img
                      src={a.image_url}
                      alt=""
                      className="w-16 h-16 object-contain shrink-0 opacity-90"
                      style={{ filter: "drop-shadow(0 0 12px rgba(255,255,255,0.18))" }}
                    />
                  )}
                </div>
                <div className="hud-divider my-4" />
                <div className="grid grid-cols-5 gap-1">
                  {a.axes.map((ax) => (
                    <div
                      key={ax.pillar}
                      className={`text-center py-1 border rounded ${
                        ax.dominant ? "border-white/50 text-white" : "border-white/10 text-mute"
                      }`}
                    >
                      <div className="gps-mono text-[11px]">{ax.letter}</div>
                    </div>
                  ))}
                </div>
              </GlassPanel>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
